import { useEffect, useRef, useState } from 'react';

export interface CorrectionEditorProps {
  tableResultId: number;
  /** The correction already on record for this hand, or null when none has been written. */
  correction: string | null;
  /** Persists the correction; null clears it. Rejects on failure — the draft is then kept. */
  onSave: (correction: string | null) => Promise<void>;
  /**
   * Reports whether the draft differs from the saved correction, so the page can hold navigation
   * back until the editor saves (or clears) an unsaved draft.
   */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * Set by the page when it has just blocked a navigation because of the unsaved draft: the field
   * turns red and a reminder appears beneath it.
   */
  remindUnsaved?: boolean;
  /**
   * Overrides the wrapper's default top chrome (`mt-4 border-t … pt-3`). Passed when the editor is
   * the first thing in its column, so it doesn't lead with a divider rule against nothing.
   */
  className?: string;
}

/**
 * The editor's correction for a mismatched hand (feature 025-ai-predicted-bids): a free-text
 * explanation of what the AI got wrong, shared with every reviewer of this hand. Rendered only
 * when the hand has a divergence — an exact match has nothing to correct.
 *
 * <p>
 * Modelled on the Ask Jeff comment editor: an explicit Save button, a confirmed (not optimistic)
 * save, and a blank draft that clears the correction. It lives inside the cream explanation panel,
 * so it wears that panel's section chrome rather than a card of its own.
 * </p>
 */
export function CorrectionEditor({
  tableResultId,
  correction,
  onSave,
  onDirtyChange,
  remindUnsaved = false,
  className = 'mt-4 border-t border-line pt-3',
}: CorrectionEditorProps) {
  const [draft, setDraft] = useState(correction ?? '');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Dirty when the trimmed draft differs from the saved value. Clearing a never-saved note back to
  // empty counts as clean — that is the natural way to discard an accidental edit and move on.
  const isDirty = draft.trim() !== (correction ?? '').trim();

  // The freshest correction, without making it an effect dependency (see below).
  const latest = useRef(correction);
  latest.current = correction;

  // Re-seed the draft only when the panel moves to ANOTHER HAND — otherwise Previous/Next would
  // carry one hand's text onto the next and risk saving it against the wrong id. (The page holds
  // navigation back while a draft is unsaved, so by the time this re-seeds the draft was saved,
  // cleared, or the reviewer chose to leave.)
  //
  // Keyed on `tableResultId` alone, deliberately: including `correction` also fired this on our
  // OWN save (the page patches the detail in place, so the prop changes), and `setStatus(null)`
  // would wipe the confirmation in the same commit that set it.
  useEffect(() => {
    setDraft(latest.current ?? '');
    setStatus(null);
    setError(null);
  }, [tableResultId]);

  // Tell the page whether there is an unsaved draft, so it can guard navigation away from the hand.
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // When the page blocks a navigation, bring the field into view and focus it so the reminder
  // beneath it is seen even if the reviewer clicked a control far from it.
  useEffect(() => {
    if (remindUnsaved) {
      textareaRef.current?.focus();
      textareaRef.current?.scrollIntoView?.({ block: 'center' });
    }
  }, [remindUnsaved]);

  // Empty the box. A local edit only — Save still persists (a blank Save removes the stored
  // correction). On a hand with a saved correction this leaves the draft "dirty" until saved; on a
  // never-saved draft it returns to clean, which doubles as the discard-and-leave path.
  const handleClear = () => {
    setDraft('');
    setStatus(null);
    setError(null);
    textareaRef.current?.focus();
  };

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    setError(null);
    const trimmed = draft.trim();
    try {
      await onSave(trimmed === '' ? null : trimmed);
      setStatus(
        trimmed === ''
          ? 'Correction cleared. This hand no longer shows an editor correction.'
          : 'Correction saved. Every reviewer of this hand will see it.',
      );
    } catch {
      setError('Could not save the correction. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={className} data-testid="correction-editor">
      <label
        htmlFor="prediction-correction"
        className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.09em] text-ink-soft"
      >
        Correction (editor)
      </label>
      <textarea
        id="prediction-correction"
        ref={textareaRef}
        value={draft}
        onChange={e => {
          setDraft(e.target.value);
          // A fresh edit makes any prior "Saved" confirmation stale.
          if (status) setStatus(null);
        }}
        rows={4}
        disabled={saving}
        data-testid="correction-textarea"
        aria-invalid={remindUnsaved || undefined}
        aria-describedby={remindUnsaved ? 'correction-unsaved-reminder' : undefined}
        className={`w-full rounded-md border bg-paper px-3 py-2 text-[14px] text-ink focus:outline-none focus-visible:ring-2 disabled:opacity-60 ${
          remindUnsaved
            ? 'border-red-500 focus-visible:ring-red-500'
            : 'border-line-strong focus-visible:ring-brass'
        }`}
        placeholder="Explain what the AI got wrong and what the correct reasoning is…"
      />

      {remindUnsaved && (
        <p
          id="correction-unsaved-reminder"
          className="mt-2 text-sm font-medium text-red-700"
          role="alert"
          data-testid="correction-unsaved-reminder"
        >
          You haven&rsquo;t saved this correction. Click &ldquo;Save correction&rdquo; to keep it
          &mdash; or clear the box &mdash; before leaving this hand.
        </p>
      )}

      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          data-testid="correction-save"
          className="rounded-md border border-brass bg-cream px-4 py-2 text-sm font-medium text-ink hover:bg-paper disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save correction'}
        </button>
        <button
          type="button"
          onClick={handleClear}
          disabled={saving || draft.trim() === ''}
          data-testid="correction-clear"
          className="rounded-md border border-line-strong bg-paper px-4 py-2 text-sm font-medium text-ink-soft hover:border-brass hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-brass disabled:cursor-not-allowed disabled:opacity-50"
        >
          Clear
        </button>
      </div>

      {status && (
        <p className="mt-2 text-sm text-green-800" role="status" data-testid="correction-status">
          {status}
        </p>
      )}
      {error && (
        <p className="mt-2 text-sm text-red-700" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
